/**
 * 国家热力图子组件
 * 对应板块: 板块 E — 在投广告数分国家
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { Icon } from 'tea-component';
import EChartCommon from '@op/components/EChartCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_V, DBO_TM } from '@op/utils/format';
import useLanguage from '@op/hooks/useLanguage';
import type {
  ICountryHeatmapProps,
} from './types';
import { countryNameToAlphaCode, getCountryDisplayName, normalizeCountryCode } from './types';
import { importJSON } from '@intelligence/utils/echartsUtils';
import CardCommon from '@op/components/CardCommon';
import { getChartMetricLabelKey } from '../../utils/metricLabels';

/**
 * 将 step 向上取整到"好看"的数字（10 / 100 / 1K / 10K ...）
 */
function roundStepUp(raw: number): number {
  if (raw <= 0) return 1;
  if (raw <= 10) return Math.ceil(raw);
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  return Math.ceil(raw / magnitude) * magnitude;
}

function buildPieces(minValue: number, maxValue: number) {
  if (!maxValue) {
    return [
      { min: 0, max: 0, label: '0', color: '#F2F3F5' },
    ];
  }

  const colors = ['#DCE7FD', '#B9CFFA', '#96B6F8', '#739EF5', '#2F6FEB'];
  const rangeMin = Math.max(1, minValue);
  const rawStep = (maxValue - rangeMin) / 5;
  const step = roundStepUp(rawStep);
  const base = Math.max(1, Math.floor(rangeMin / step) * step);
  const ceil = Math.ceil(maxValue / step) * step;

  return colors
    .map((color, index) => {
      const min = base + index * step;
      const max = index === colors.length - 1 ? ceil : base + (index + 1) * step;

      return {
        min: Math.max(1, min),
        max,
        label: `${DBO_V.reviews(Math.max(1, min), 2, '0')} - ${DBO_V.reviews(max, 2, '0')}`,
        color,
      };
    })
    .filter((piece) => piece.min <= maxValue)
    .reverse();
}

const mapCache: { worldMap: any; } = { worldMap: null };

// ═══════════════════════════════════════════════════════════════════
// MapRenderer — React.memo pure chart component, renders map ONCE.
// All props are stable refs → memo always bails out → zero re-renders.
// ═══════════════════════════════════════════════════════════════════
interface IMapRendererProps {
  chartRef: React.MutableRefObject<any>;
  initialOption: any;
  loading: boolean;
  optionsProps: any;
  eventsDict: any;
}

const MapRendererInner: React.FC<IMapRendererProps> = ({ chartRef, initialOption, loading, optionsProps, eventsDict }) => {
  return (
    <EChartCommon
      ref={chartRef}
      options={initialOption}
      height={400}
      loading={loading}
      optionsProps={optionsProps}
      eventsDict={eventsDict}
    />
  );
};

MapRendererInner.displayName = 'MapRenderer';

// Custom comparator: only re-render when loading changes
const MapRenderer = React.memo(
  MapRendererInner,
  (prev, next) => prev.loading === next.loading,
);

// ═══════════════════════════════════════════════════════════════════
// CountryHeatmap — handles data logic, uses setOption for incremental
// updates. MapRenderer child only mounts once.
// ═══════════════════════════════════════════════════════════════════
const CountryHeatmap: React.FC<ICountryHeatmapProps> = ({
  data,
  loading,
  selectedMapName = '',
  onCountryClick,
  crossAnalysis,
  crossMetricLatestMap = {},
  dataDate,
  dateType = 'daily',
  filter,
}) => {
  const chartRef = useRef<any>(null);
  const isMounted = useRef(true);
  const [mapLoading, setMapLoading] = useState(true);
  const { formatMessage, isEn } = useLanguage();

  // Refs to hold latest values — tooltip formatter & click handler
  // read from here to always get fresh props without re-creating closures
  const propsRef = useRef({
    crossAnalysis, crossMetricLatestMap, dataDate, dateType,
    formatMessage, isEn, onCountryClick, filter,
  });
  propsRef.current = {
    crossAnalysis, crossMetricLatestMap, dataDate, dateType,
    formatMessage, isEn, onCountryClick, filter,
  };

  // Load world map JSON (once)
  useEffect(() => {
    const loadMapData = async () => {
      try {
        if (mapCache.worldMap || echarts.getMap('world')) {
          if (isMounted.current) setMapLoading(false);
          return;
        }
        const mapData = await importJSON('https://ogdb-cdn.intlgame.cn/front/static/world.json');
        if (mapData && isMounted.current) {
          mapCache.worldMap = mapData;
          echarts.registerMap('world', mapData as any);
          setMapLoading(false);
        }
      } catch (e) {
        console.error('[NewWorldMap] Failed to load map data:', e);
        if (isMounted.current) setMapLoading(false);
      }
    };

    loadMapData();
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // Incremental data update via setOption — map never re-renders
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    if (!chartInstance || loading) return;
    const isCrossAnalysis = crossAnalysis?.enabled === true;
    const seriesData = data?.map((item) => ({
      name: item.mapName,
      value: item.value,
      countryName: item.countryName,
      dimensionValue: item.dimensionValue,
      alpha2Code: item.alpha2Code,
    })) || [];


    if (isCrossAnalysis && !seriesData.some((item) => item.name === selectedMapName)) {
      const selectedAlpha2 = normalizeCountryCode(selectedMapName);
      seriesData.push({
        name: selectedMapName,
        value: 0,
        countryName: getCountryDisplayName(selectedMapName, isEn),
        dimensionValue: '',
        alpha2Code: selectedAlpha2,
      });
    }

    const values = seriesData.map((item) => item.value || 0).filter((v) => v > 0);
    const maxValue = values.length ? Math.max(...values) : 0;
    const minValue = values.length ? Math.min(...values) : 0;
    const pieces = buildPieces(minValue, maxValue);

    chartInstance.setOption({
      visualMap: { pieces },
      graphic: [{
        type: 'text',
        left: 0,
        bottom: pieces.length * 24 + 10,
        style: { text: formatMessage(getChartMetricLabelKey('Active Ads', filter as any)), fill: '#838697', fontSize: 12, fontWeight: 500 },
      }],
      series: {
        type: 'map',
        data: seriesData,
        map: 'world',
        geoIndex: 0,
        selectedMode: 'single',
        emphasis: { itemStyle: { areaColor: '#7FA8F7' } },
        select: {
          itemStyle: { areaColor: '#5086F3', borderColor: '#335DB8', borderWidth: 1.5 },
        },
      },
    }, {
      replaceMerge: ['series', 'graphic']
    });

    // Sync selection state AFTER setOption (replaceMerge clears internal selection)
    if (isCrossAnalysis && selectedMapName) {
      chartInstance.dispatchAction({
        type: 'select',
        seriesIndex: 0,
        name: selectedMapName,
      });
    }
  }, [data, selectedMapName, crossAnalysis?.enabled, isEn, formatMessage, loading, filter]);

  // When cross-analysis is disabled, unselect all
  useEffect(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    if (!chartInstance || crossAnalysis?.enabled) return;

    chartInstance.dispatchAction({
      type: 'unselect',
      seriesIndex: 0,
      dataIndex: (data || []).map((_: any, i: number) => i),
    });
  }, [crossAnalysis?.enabled]);

  const initialOptionRef = useRef<any>(null);
  if (!initialOptionRef.current) {
    initialOptionRef.current = {
      tooltip: {
        padding: 0,
        borderWidth: 0,
        formatter: (params: any) => {
          if (!params?.data || params.data.value === null || params.data.value === undefined) return '';
          const { crossAnalysis: ca, crossMetricLatestMap: cm, dataDate: dd, dateType: dt, formatMessage: fm, isEn: en, filter } = propsRef.current;
          const countryName = params.data.countryName
            || getCountryDisplayName(params.name, en)
            || params.name;
          const records: Array<{ text: string; value: string; }> = [
            { text: fm(getChartMetricLabelKey('Active Ads', filter as any)), value: String(DBO_V.reviews(params.data.value || 0)) },
          ];

          // if (ca?.enabled && ca.metric && dimValue) {
          //   const crossValue = cm[dimValue];
          //   records.push({
          //     text: fm(CROSS_METRIC_TEXT_MAP[ca.metric]),
          //     value: crossValue !== null && crossValue !== undefined ? String(DBO_V.reviews(crossValue)) : '-',
          //   });
          // }

          return formatChartParamsDate({
            title: countryName,
            rightTitle: dd ? DBO_TM.formatFunc(dt)(dd) : '',
            params: [params],
            tablesConfigFunc: () => [{
              showHeader: false,
              records,
              columns: [
                { key: 'text' },
                { key: 'value', align: 'right', weight: 'bold' },
              ],
            }],
          });
        },
      },
      visualMap: {
        type: 'piecewise',
        orient: 'vertical',
        left: 0,
        bottom: 0,
        itemWidth: 10,
        itemHeight: 24,
        itemGap: 0,
        textGap: 4,
        textStyle: { color: '#7B8794', fontSize: 12, lineHeight: 14 },
        itemSymbol: 'rect',
        pieces: [],
      },
      title: { show: false },
      geo: {
        map: 'world',
        roam: true,
        zoom: 1,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        scaleLimit: { min: 1, max: 10 },
        itemStyle: { areaColor: '#F2F3F5', borderColor: '#00000059', borderWidth: 0.5 },
        emphasis: {
          itemStyle: { areaColor: '#7FA8F7' },
          label: { show: false },
        },
        select: {
          itemStyle: { areaColor: '#F8D74D', borderColor: '#00000059', borderWidth: 0.5 },
          label: { show: false },
        },
      },
      series: {
        type: 'map',
        map: 'world',
        geoIndex: 0,
        selectedMode: 'single',
        emphasis: { itemStyle: { areaColor: '#7FA8F7' } },
        select: {
          itemStyle: { areaColor: '#5086F3', borderColor: '#335DB8', borderWidth: 1.5 },
        },
      },
    };
  }

  // Stable refs for MapRenderer props — never change identity
  const optionsPropsRef = useRef({ type: 'map' as const, excludeOptions: ['xAxis' as const] });
  const eventsDictRef = useRef({
    click: (params: any) => {
      chartRef.current?.getEchartsInstance?.()?.dispatchAction({
        type: 'select',
        seriesIndex: 0,
        name: params.name,
      });
      if (params?.componentType === 'series' && params?.name) {
        const { onCountryClick: onClick } = propsRef.current;
        const countryCode = countryNameToAlphaCode(params.name);
        onClick?.(countryCode, params.name);
      }
    },
  });

  const handleZoom = (delta: number) => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    const currentZoom = chartInstance?.getOption?.()?.geo?.[0]?.zoom || 1;
    const nextZoom = Math.max(1, Math.min(5, currentZoom + delta));

    chartInstance?.setOption({
      geo: { zoom: nextZoom },
    });
  };

  return (
    <CardCommon className="dbo-country-heatmap" contentCard>
      <MapRenderer
        chartRef={chartRef}
        initialOption={initialOptionRef.current}
        loading={mapLoading}
        optionsProps={optionsPropsRef.current}
        eventsDict={eventsDictRef.current}
      />
      <div className="dbo-country-heatmap__controls">
        <span
          className="dbo-country-heatmap__control-btn"
          onClick={() => handleZoom(0.3)}
        >
          <Icon type="plus" />
        </span>
        <span
          className="dbo-country-heatmap__control-btn"
          onClick={() => handleZoom(-0.3)}
        >
          <Icon type="minus" />
        </span>
      </div>
    </CardCommon>
  );
};

export default CountryHeatmap;
